import { useState } from "react";
import Button from "@amzn/meridian/esm/components/button";
import Column from "@amzn/meridian/esm/components/column";
import Text from "@amzn/meridian/esm/components/text";
import WaveformLayout from "../../components/waveformLayout/WaveformLayout";
import defaultImage from "../../images/attendee_initial.svg";
import stoppedImage from "../../images/progress_stop_image.svg";
import InformSummarizeModal from "./InformSummarizeModal";
import { STATUS_BUTTON_MIN_WIDTH, SUMMARIZE_STATES } from "../../constants";
import { DEFAULT_VIEW_TEXT } from "./home.constants";
import LogManager from "../../utils/logManager";

/**
 * DefaultView - Shared view for initial state and session ended state.
 * Used for organizers (with Start button), attendees (no button), and session ended.
 *
 * Automatically determines image, title, and subtitle based on summarizeState; the stopped copy
 * comes from the flow (flowText). showStartButton controls whether the Start button is rendered,
 * showInformModal whether the first Start asks for confirmation (summary flow only).
 * onStart starts the session (from the summarization controller).
 */
const DefaultView = ({
  summarizeState,
  summarizeClickCount,
  onStart,
  flowText,
  showStartButton = true,
  showInformModal = true,
}) => {
  // Determine display based on summarize state
  const isSessionEnded = summarizeState === SUMMARIZE_STATES.STOPPED;
  const image = isSessionEnded ? stoppedImage : defaultImage;
  const title = isSessionEnded ? DEFAULT_VIEW_TEXT.STOPPED_TITLE : DEFAULT_VIEW_TEXT.DEFAULT_TITLE;
  const subtitles = isSessionEnded ? flowText.STOPPED_SUBTITLES : (!showStartButton ? [DEFAULT_VIEW_TEXT.ATTENDEE_SUBTITLE] : []);

  const [isInformSummarizeModalOpen, setIsInformSummarizeModalOpen] = useState(false);

  const handleStartClick = () => {
    LogManager.metric("Summarization_StartButton_Click", 1);
    if (showInformModal && summarizeClickCount === 0) {
      setIsInformSummarizeModalOpen(true);
    } else {
      startSummarize();
    }
  };

  const onModalConfirmed = () => {
    setIsInformSummarizeModalOpen(false);
    startSummarize();
  };

  const startSummarize = () => {
    onStart();
  };

  return (
    <>
      <WaveformLayout image={image}>
        <Column spacing="300">
          <Text type="h50">{title}</Text>
          {subtitles.map((subtitle) => (
            <Text key={subtitle} type="b200" color="secondary">
              {subtitle}
            </Text>
          ))}
        </Column>
        {showStartButton && (
          <Button onClick={handleStartClick} minWidth={STATUS_BUTTON_MIN_WIDTH} size="small">
            Start
          </Button>
        )}
      </WaveformLayout>
      {showStartButton && showInformModal && (
        <InformSummarizeModal
          isOpen={isInformSummarizeModalOpen}
          onClose={() => setIsInformSummarizeModalOpen(false)}
          onConfirm={onModalConfirmed}
        />
      )}
    </>
  );
};

export default DefaultView;
